/**
 * Jurisdiction Health Report Page
 * ================================
 * 
 * Admin tool to identify and fix jurisdiction data issues:
 * - Missing parent county for sub-jurisdictions
 * - Missing state
 * - Duplicates
 * - State mismatches
 * - Inactive parent references
 * 
 * Features:
 * - Summary cards with health metrics
 * - Filterable table with issue indicators
 * - Fix Parent action (guided modal)
 * - Fix State action (guided modal)
 * - Duplicate review
 * 
 * Note: Bulk upload is handled via Jurisdiction Directory page only.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Wrench,
  MapPin,
  Building2,
  FileWarning,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Issue badge colors
const ISSUE_COLORS = {
  missing_state: 'bg-red-100 text-red-800',
  missing_parent: 'bg-red-100 text-red-800',
  invalid_parent: 'bg-red-100 text-red-800',
  state_mismatch: 'bg-amber-100 text-amber-800',
  inactive_parent: 'bg-amber-100 text-amber-800',
  duplicate: 'bg-purple-100 text-purple-800',
  missing_research_links: 'bg-sky-100 text-sky-800'
};

const ISSUE_LABELS = {
  missing_state: 'Missing State',
  missing_parent: 'Missing Parent',
  invalid_parent: 'Invalid Parent',
  state_mismatch: 'State Mismatch',
  inactive_parent: 'Inactive Parent',
  duplicate: 'Duplicate',
  missing_research_links: 'No Research Links'
};

const STATUS_COLORS = {
  healthy: 'bg-green-100 text-green-800',
  needs_review: 'bg-amber-100 text-amber-800',
  broken: 'bg-red-100 text-red-800'
};

const STATUS_ICONS = {
  healthy: <CheckCircle className="h-4 w-4 text-green-600" />,
  needs_review: <AlertCircle className="h-4 w-4 text-amber-600" />,
  broken: <XCircle className="h-4 w-4 text-red-600" />
};

export default function JurisdictionHealthReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [error, setError] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    state: '',
    jurisdiction_type: '',
    active_only: '',
    used_only: false,
    search: ''
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  
  // Fix Parent Modal
  const [fixParentModal, setFixParentModal] = useState({ open: false, jurisdiction: null });
  const [availableCounties, setAvailableCounties] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState('');
  const [fixingParent, setFixingParent] = useState(false);
  
  // Fix State Modal
  const [fixStateModal, setFixStateModal] = useState({ open: false, jurisdiction: null });
  const [newState, setNewState] = useState('');
  const [fixingState, setFixingState] = useState(false);
  
  const token = localStorage.getItem('token');
  
  // Fetch health report
  const fetchHealthReport = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (filters.status) params.append('status', filters.status);
      if (filters.state) params.append('state', filters.state);
      if (filters.jurisdiction_type) params.append('jurisdiction_type', filters.jurisdiction_type);
      if (filters.active_only === 'true') params.append('active_only', 'true');
      if (filters.active_only === 'false') params.append('active_only', 'false');
      if (filters.used_only) params.append('used_only', 'true');
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${API}/api/jurisdictions/health?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch health report');
      }
      
      const data = await response.json();
      setHealthData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load health report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, page, pageSize, filters]);
  
  useEffect(() => {
    fetchHealthReport();
  }, [fetchHealthReport]);
  
  // Jun 2026 — LA terminology: Louisiana's county-equivalent level is the
  // Parish. All Fix-Parent labels/messages must read "Parish" for LA rows.
  // Mirrors backend/models/jurisdiction.py::get_parent_terminology().
  const parentNounForState = (state) => ((state || '').toUpperCase() === 'LA' ? 'Parish' : 'County');

  // Fetch counties for Fix Parent modal
  const fetchCountiesForState = async (state) => {
    try {
      const response = await fetch(
        // `parent_eligible=true` resolves to the correct county-equivalent
        // types per state (county + LA Parish + AK/NY Borough) — same
        // canonical filter the Jurisdiction Wizard uses. Jun 2026 fix:
        // was hard-coded `jurisdiction_type=county`, which left the
        // dropdown empty for LA rows (LA has zero county-typed records).
        `${API}/api/jurisdictions?parent_eligible=true&state=${state}&active_only=true&page_size=500`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAvailableCounties(data.jurisdictions || []);
      }
    } catch (err) {
      console.error('Error fetching counties:', err);
    }
  };
  
  // Open Fix Parent Modal
  const openFixParentModal = (jurisdiction) => {
    setFixParentModal({ open: true, jurisdiction });
    setSelectedParentId(jurisdiction.parent_county_id || '');
    if (jurisdiction.state) {
      fetchCountiesForState(jurisdiction.state);
    } else {
      setAvailableCounties([]);
    }
  };
  
  // Handle Fix Parent
  const handleFixParent = async () => {
    if (!selectedParentId) {
      toast.error(`Please select a parent ${parentNounForState(fixParentModal.jurisdiction?.state).toLowerCase()}`);
      return;
    }
    
    setFixingParent(true);
    try {
      const response = await fetch(
        `${API}/api/jurisdictions/health/${fixParentModal.jurisdiction.id}/fix-parent`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ parent_county_id: selectedParentId })
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fix parent');
      }
      
      toast.success(`Parent ${parentNounForState(fixParentModal.jurisdiction?.state).toLowerCase()} updated successfully`);
      setFixParentModal({ open: false, jurisdiction: null });
      fetchHealthReport(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFixingParent(false);
    }
  };
  
  // Open Fix State Modal
  const openFixStateModal = (jurisdiction) => {
    setFixStateModal({ open: true, jurisdiction });
    setNewState(jurisdiction.state || '');
  };
  
  // Handle Fix State
  const handleFixState = async () => {
    if (!newState || newState.length !== 2) {
      toast.error('Please enter a valid 2-letter state code');
      return;
    }
    
    setFixingState(true);
    try {
      const response = await fetch(
        `${API}/api/jurisdictions/health/${fixStateModal.jurisdiction.id}/fix-state`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ state: newState.toUpperCase() })
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fix state');
      }
      
      toast.success('State updated successfully');
      setFixStateModal({ open: false, jurisdiction: null });
      fetchHealthReport(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFixingState(false);
    }
  };
  
  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };
  
  // Summary card component
  const SummaryCard = ({ title, value, icon, color, onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-sm border p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
          {icon}
        </div>
      </div>
    </div>
  );
  
  if (loading && !healthData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-green-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading health report...</p>
        </div>
      </div>
    );
  }
  
  const summary = healthData?.summary || {};
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/jurisdictions')}
                className="flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FileWarning className="h-5 w-5 text-amber-600" />
                  Jurisdiction Health Report
                </h1>
                <p className="text-sm text-gray-500">Identify and fix data integrity issues</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchHealthReport(true)}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <SummaryCard
            title="Total"
            value={summary.total_jurisdictions || 0}
            icon={<Building2 className="h-5 w-5 text-blue-600" />}
            color="text-blue-600"
          />
          <SummaryCard
            title="Healthy"
            value={summary.healthy || 0}
            icon={<CheckCircle className="h-5 w-5 text-green-600" />}
            color="text-green-600"
            onClick={() => handleFilterChange('status', 'healthy')}
          />
          <SummaryCard
            title="Needs Review"
            value={summary.needs_review || 0}
            icon={<AlertCircle className="h-5 w-5 text-amber-600" />}
            color="text-amber-600"
            onClick={() => handleFilterChange('status', 'needs_review')}
          />
          <SummaryCard
            title="Broken"
            value={summary.broken || 0}
            icon={<XCircle className="h-5 w-5 text-red-600" />}
            color="text-red-600"
            onClick={() => handleFilterChange('status', 'broken')}
          />
          <SummaryCard
            title="Missing Parent"
            value={summary.missing_parent || 0}
            icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
            color="text-red-600"
          />
          <SummaryCard
            title="Missing State"
            value={summary.missing_state || 0}
            icon={<MapPin className="h-5 w-5 text-red-600" />}
            color="text-red-600"
          />
          <SummaryCard
            title="No Research Links"
            value={summary.missing_research_links || 0}
            icon={<ExternalLink className="h-5 w-5 text-sky-600" />}
            color="text-sky-600"
          />
        </div>
        
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="healthy">Healthy</SelectItem>
                  <SelectItem value="needs_review">Needs Review</SelectItem>
                  <SelectItem value="broken">Broken</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">State</Label>
              <Input
                placeholder="e.g. PA"
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value.toUpperCase())}
                maxLength={2}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={filters.jurisdiction_type} onValueChange={(v) => handleFilterChange('jurisdiction_type', v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="county">County</SelectItem>
                  <SelectItem value="city">City</SelectItem>
                  <SelectItem value="township">Township</SelectItem>
                  <SelectItem value="village">Village</SelectItem>
                  <SelectItem value="school_district">School District</SelectItem>
                  <SelectItem value="borough">Borough</SelectItem>
                  <SelectItem value="special_district">Special District</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Active</Label>
              <Select value={filters.active_only} onValueChange={(v) => handleFilterChange('active_only', v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="true">Active Only</SelectItem>
                  <SelectItem value="false">Inactive Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
                <Input
                  placeholder="Name..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="h-9 pl-8"
                />
              </div>
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters({ status: '', state: '', jurisdiction_type: '', active_only: '', used_only: false, search: '' });
                  setPage(1);
                }}
                className="text-gray-500"
              >
                Clear All
              </Button>
            </div>
          </div>
        </div>
        
        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="tf-thead">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jurisdiction Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent County / Parish</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {healthData?.jurisdictions?.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No jurisdictions match the current filters
                    </td>
                  </tr>
                ) : (
                  healthData?.jurisdictions?.map((j) => (
                    <tr key={j.id} className={`hover:bg-gray-50 ${!j.is_active ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[j.status]}`}>
                          {STATUS_ICONS[j.status]}
                          {j.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-mono text-sm ${!j.state ? 'text-red-500 italic' : ''}`}>
                          {j.state || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">{j.jurisdiction_type}</td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-sm">{j.jurisdiction_name}</span>
                        {!j.is_active && (
                          <span className="ml-2 text-xs text-gray-400">(inactive)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {j.parent_county_name || (
                          j.jurisdiction_type !== 'county' ? (
                            <span className="text-red-500 italic">Not set</span>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {j.issues.length === 0 ? (
                            <span className="text-xs text-gray-400">None</span>
                          ) : (
                            j.issues.map((issue) => (
                              <span
                                key={issue}
                                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${ISSUE_COLORS[issue]}`}
                              >
                                {ISSUE_LABELS[issue]}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {j.order_count > 0 ? (
                          <span className="font-medium text-blue-600">{j.order_count}</span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {j.issues.includes('missing_parent') && j.state && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openFixParentModal(j)}
                              className="text-green-600 hover:text-green-700 h-7 px-2"
                            >
                              <Wrench className="h-3 w-3 mr-1" />
                              Fix Parent
                            </Button>
                          )}
                          {j.issues.includes('missing_state') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openFixStateModal(j)}
                              className="text-blue-600 hover:text-blue-700 h-7 px-2"
                            >
                              <MapPin className="h-3 w-3 mr-1" />
                              Set State
                            </Button>
                          )}
                          {j.issues.length === 0 && (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {healthData && healthData.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="text-sm text-gray-500">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, healthData.total)} of {healthData.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {healthData.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(healthData.total_pages, p + 1))}
                  disabled={page === healthData.total_pages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Fix Parent Modal */}
      <Dialog open={fixParentModal.open} onOpenChange={(open) => !open && setFixParentModal({ open: false, jurisdiction: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fix Parent {parentNounForState(fixParentModal.jurisdiction?.state)}</DialogTitle>
            <DialogDescription>
              Select the parent {parentNounForState(fixParentModal.jurisdiction?.state).toLowerCase()} for "{fixParentModal.jurisdiction?.jurisdiction_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Jurisdiction</Label>
              <p className="text-sm font-medium">{fixParentModal.jurisdiction?.jurisdiction_name}</p>
              <p className="text-xs text-gray-500">
                Type: {fixParentModal.jurisdiction?.jurisdiction_type} | State: {fixParentModal.jurisdiction?.state || 'N/A'}
              </p>
            </div>
            {!fixParentModal.jurisdiction?.state ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-800">
                  ⚠️ This jurisdiction is missing a state. Please set the state first before assigning a parent county.
                </p>
              </div>
            ) : (
              <div>
                <Label>Parent {parentNounForState(fixParentModal.jurisdiction?.state)}</Label>
                <Select value={selectedParentId} onValueChange={setSelectedParentId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={availableCounties.length === 0
                      ? `No ${parentNounForState(fixParentModal.jurisdiction?.state).toLowerCase() === 'parish' ? 'parishes' : 'counties'} available`
                      : `Select parent ${parentNounForState(fixParentModal.jurisdiction?.state).toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCounties.map((county) => (
                      <SelectItem key={county.id} value={county.id}>
                        {county.jurisdiction_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableCounties.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No {parentNounForState(fixParentModal.jurisdiction?.state).toLowerCase() === 'parish' ? 'parishes' : 'counties'} found for state {fixParentModal.jurisdiction?.state}. Please upload {parentNounForState(fixParentModal.jurisdiction?.state).toLowerCase() === 'parish' ? 'parishes' : 'counties'} first.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixParentModal({ open: false, jurisdiction: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleFixParent} 
              disabled={fixingParent || !selectedParentId || !fixParentModal.jurisdiction?.state}
            >
              {fixingParent ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Fix State Modal */}
      <Dialog open={fixStateModal.open} onOpenChange={(open) => !open && setFixStateModal({ open: false, jurisdiction: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set State</DialogTitle>
            <DialogDescription>
              Enter the 2-letter state code for "{fixStateModal.jurisdiction?.jurisdiction_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>State Code</Label>
              <Input
                placeholder="e.g. PA, NJ, NY"
                value={newState}
                onChange={(e) => setNewState(e.target.value.toUpperCase())}
                maxLength={2}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Enter 2-letter US state code</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixStateModal({ open: false, jurisdiction: null })}>
              Cancel
            </Button>
            <Button onClick={handleFixState} disabled={fixingState || newState.length !== 2}>
              {fixingState ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
